// ═══════════════════════════════════════════════════════════
// OIA widget loader — the standard layout (skill: oia-license-server).
//
// `widget.js` is loaded ONCE with `data-manual-init`; one instance per job:
//   support  — launcher, view 'support', support-scope token (bare inbox)
//   billing  — popup, opened only by OIAWidget.changePlan(slug)
//   account  — optional embed on Settings → License (full billing account)
// This app authenticates with request headers, not cookies, so each instance
// gets its token through `getToken` → our `POST /api/oia/widget-token`.
// Host config is looks/placement only; tabs, copy, brand etc. are server-owned.
// The browser never sees the API key. Do NOT build billing/ticket UI by hand.
// ═══════════════════════════════════════════════════════════
import { api } from "@/lib/api-client";

interface WidgetInstance {
  open?: (tab?: string, opts?: Record<string, unknown>) => void;
  destroy?: () => void;
}
type WidgetFn = ((...args: unknown[]) => unknown) & {
  q?: unknown[][];
  changePlan?: (slug: string, opts?: { promo?: string }) => void;
  get?: (id: string) => WidgetInstance | undefined;
};
declare global {
  interface Window {
    OIAWidget?: WidgetFn;
  }
}

interface OiaConfig {
  enabled: boolean;
  serverUrl: string | null;
}

interface WidgetToken {
  token: string;
  refreshToken: string;
}

type TokenScope = "full" | "support";

/** Fetches a widget token from OUR backend, sending the app's own auth headers. */
export function getOiaWidgetToken(scope: TokenScope = "full"): Promise<WidgetToken> {
  return api.raw<WidgetToken>(
    scope === "support" ? "/api/oia/widget-token?scope=support" : "/api/oia/widget-token",
    { method: "POST", headers: { "X-OIA-Widget-Request": "1" } },
  );
}

let loading: Promise<boolean> | null = null;

/** Injects widget.js once. Resolves false (renders nothing) when the integration is off. */
export function loadOiaWidget(): Promise<boolean> {
  if (loading) return loading;
  loading = (async () => {
    try {
      const cfg = await api.raw<OiaConfig>("/api/oia/config");
      const origin = cfg.serverUrl;
      if (!cfg.enabled || !origin) return false;

      // Queue stub BEFORE the script so early calls are not lost.
      const stub: WidgetFn = (...args: unknown[]) => {
        (stub.q = stub.q ?? []).push(args);
      };
      window.OIAWidget = window.OIAWidget ?? stub;

      await new Promise<void>((resolve, reject) => {
        const s = document.createElement("script");
        s.src = `${origin}/widget.js`;
        s.async = true;
        s.setAttribute("data-manual-init", "");
        s.onload = () => {
          resolve();
        };
        s.onerror = () => {
          reject(new Error("widget.js failed to load"));
        };
        document.head.appendChild(s);
      });
      return true;
    } catch {
      loading = null; // allow a retry on the next mount
      return false;
    }
  })();
  return loading;
}

let sharedInstancesFor: string | null = null;

/**
 * Creates the two page-wide instances, Support first (global calls such as
 * `OIAWidget('on', …)` reach the first instance created). Idempotent per member.
 * `onPlanChanged` runs when a plan change has completed (re-read entitlements).
 */
export async function initOiaSharedWidgets(
  memberId: string,
  onPlanChanged: () => void,
): Promise<void> {
  if (sharedInstancesFor === memberId) return;
  if (!(await loadOiaWidget()) || !window.OIAWidget) return;
  sharedInstancesFor = memberId;
  const w = window.OIAWidget;

  w("init", {
    id: "support",
    mode: "launcher",
    view: "support",
    getToken: () => getOiaWidgetToken("support"),
    sessionKey: `${memberId}:support`,
    // Looks / placement only. The onboarding rocket owns bottom-right → edge tab.
    config: { launchers: { support: { position: "right-edge" } }, theme: { mode: "auto" } },
  });
  w("init", {
    id: "billing",
    mode: "popup",
    getToken: () => getOiaWidgetToken("full"),
    sessionKey: `${memberId}:billing`,
    config: { theme: { mode: "auto" } },
  });
  // Unlock features only on `plan-changed`, never on the popup closing.
  w("on", "plan-changed", onPlanChanged);
}

/**
 * Hand the customer to the widget's plan-change popup (subscribe / renew / upgrade /
 * downgrade). The SERVER decides the mode and the price; the app never computes either.
 */
export async function changePlan(slug: string, opts?: { promo?: string }): Promise<void> {
  if (!(await loadOiaWidget()) || !window.OIAWidget) return;
  const w = window.OIAWidget;
  if (typeof w.changePlan === "function") {
    w.changePlan(slug, opts);
    return;
  }
  // Server predates the changePlan contract: fall back to the account instance's Plans tab.
  w.get?.("account")?.open?.("plans", { highlight: slug });
}

/**
 * Mounts the optional account embed (full billing account) into `containerId`.
 * Synchronous on purpose: call it only after `loadOiaWidget()` resolved true AND the
 * caller's effect was not cancelled — creating then destroying an instance under React
 * StrictMode's double effect leaves the embed blank.
 */
export function mountOiaAccount(memberId: string, containerId: string): void {
  window.OIAWidget?.("init", {
    id: "account",
    mode: "embed",
    container: containerId,
    getToken: () => getOiaWidgetToken("full"),
    sessionKey: `${memberId}:account`,
    // No config: the server decides which tabs show.
  });
}

export function destroyOiaAccount(): void {
  window.OIAWidget?.get?.("account")?.destroy?.();
}
